import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './ForgotPasswordForm.css';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ForgotPasswordFormData>({
    email: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = '請輸入 Email';
    } else if (!formData.email.includes('@')) {
      newErrors.email = '請輸入有效的 Email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await api.post('/api/members/forgot-password', {
        email: formData.email.toLowerCase().trim(),
      });

      if (result.success) {
        setSubmitSuccess(true);
      } else {
        setSubmitError(result.error?.message || '發送失敗，請稍後再試。');
      }
    } catch {
      setSubmitError('發生錯誤，請稍後再試。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  const handleGoToReset = useCallback(() => {
    navigate(`/reset-password?email=${encodeURIComponent(formData.email)}`);
  }, [navigate, formData.email]);

  if (submitSuccess) {
    return (
      <div className="forgot-password-form">
        <div className="forgot-password-form__header">
          <div className="forgot-password-form__success-icon">✉️</div>
          <h2 className="forgot-password-form__title">驗證碼已發送</h2>
        </div>

        <div className="forgot-password-form__success-message">
          <p>
            如果 <strong>{formData.email}</strong> 已註冊，您將收到包含 6 位數驗證碼的 Email。
          </p>
          <p>請檢查您的收件匣並使用驗證碼重設密碼。</p>
        </div>

        <div className="forgot-password-form__actions">
          <button
            type="button"
            className="forgot-password-form__secondary-button"
            onClick={() => setSubmitSuccess(false)}
          >
            重新發送
          </button>
          <button
            type="button"
            className="forgot-password-form__primary-button"
            onClick={handleGoToReset}
          >
            前往重設密碼
          </button>
        </div>

        <div className="forgot-password-form__footer">
          <button
            type="button"
            className="forgot-password-form__back-link"
            onClick={handleBackToLogin}
          >
            ← 返回登入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-form">
      <div className="forgot-password-form__header">
        <h2 className="forgot-password-form__title">忘記密碼</h2>
        <p className="forgot-password-form__subtitle">
          輸入您的 Email 以接收密碼重設驗證碼
        </p>
      </div>

      <form onSubmit={handleSubmit} className="forgot-password-form__form">
        {submitError && (
          <div className="forgot-password-form__error" role="alert">
            {submitError}
          </div>
        )}

        <div className="forgot-password-form__field">
          <label htmlFor="email" className="forgot-password-form__label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`forgot-password-form__input ${
              errors.email ? 'forgot-password-form__input--error' : ''
            }`}
            placeholder="your@email.com"
            disabled={isSubmitting}
            autoComplete="email"
          />
          {errors.email && (
            <span className="forgot-password-form__field-error">{errors.email}</span>
          )}
        </div>

        <button
          type="submit"
          className="forgot-password-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? '發送中...' : '發送驗證碼'}
        </button>

        <div className="forgot-password-form__footer">
          <button
            type="button"
            className="forgot-password-form__back-link"
            onClick={handleBackToLogin}
          >
            ← 返回登入
          </button>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
